/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
/**
 * Create an optimal spread of elements with respect to elements that are visible,
 * and the minimum distance between elements.
 *
 * new Spread([listPropName]).spread(list, left, width, min, [listPropName]) -> list
 *
 * listPropName will use the supplied property name when calculating with a list
 * of objects.
 *
 * if a new listPropName is given to spread it will use that instead of what was
 * given to the constructor
 */
export class Spread<T> {
	private _listPropName: string;
	private _min: number;
	private _list: T[] = [];

	public static from<T>(list: T[], left: number, width: number, min: number, listPropName: string) {
		const spreader: Spread<T> = new Spread<T>(listPropName);
		return spreader.spread(list, left, width, min, listPropName);
	}

	constructor(listPropName: string) {
		this._listPropName = listPropName || null;
		this._min = 0; // will be overwritten
	}

	/**
	 * Destroy everything
	 */
	public destroy(): void {
		this._min = null;
		this._list = null;
		this._listPropName = null;
	}

	/**
	 * 	Main entry point, sets the current list and bounds. Optionally, change the
	 * 	current listPropName that is being used for value lookup.
	 * @param list list of generic thing to spread
	 * @param left the left most edge of the thing
	 * @param width the total width of all things
	 * @param min the minimum spacing between the things
	 * @param listPropName the property name to use as a spread value
	 */
	public spread(list: T[], left: number, width: number, min: number, listPropName?: string): T[] {
		// Minimum distance between points
		this._min = min || this._min;

		// Property to use for value lookup
		this._listPropName = listPropName || this._listPropName;

		// Set 'this.list' to the current list of supplied elements
		this._list = list.slice().filter((item) => {
			// filter by left/width
			const value = this.valueFor(item);
			return value > left && value < left + width;
		});

		// quick exits
		if (this._list.length === 0) {
			return [];
		}
		if (this._list.length === 1) {
			return [list[0]];
		}

		// sort low -> high
		this._list.sort((a, b) => this.valueFor(a) - this.valueFor(b));

		// Start the real work..
		const spread = [];
		let back: number = this._list.length - 1;
		for (var front: number = 0; front < back; front++, back--) {
			if (
				spread.length === 0 ||
				(Math.abs(this.valueAt(back) - this.valueFor(spread[spread.length - 1])) > min &&
					Math.abs(this.valueAt(back) - this.valueFor(spread[0])) > min)
			) {
				spread.push(this._list[back]);
			}

			if (
				Math.abs(this.valueFor(spread[0]) - this.valueAt(front)) > min &&
				Math.abs(this.valueFor(spread[spread.length - 1]) - this.valueAt(front)) > min
			) {
				spread.unshift(this._list[front]);
			}
		}

		if (
			Math.abs(this.valueAt(back) - this.valueFor(spread[spread.length - 1])) > min &&
			Math.abs(this.valueAt(back) - this.valueFor(spread[0])) > min
		) {
			spread.push(this._list[back]);
		}

		return spread;
	}

	/**
	 * Get value at index
	 * @param index index in the T[] (this._list) array
	 */
	public valueAt(index): any {
		if (this._listPropName) {
			return this._list[index][this._listPropName];
		} else {
			return this._list[index];
		}
	}

	/**
	 * retrieve value for an element
	 * @param item T object in the array
	 */
	public valueFor(item: T): any {
		if (this._listPropName) {
			return item[this._listPropName];
		} else {
			return item;
		}
	}

	/**
	 * get all values from array
	 * @param list
	 */
	public values(list: object[]): any[] {
		return (list || this._list).map((item) => this.valueFor(item));
	}

	/**
	 * grab the element closest to a particular value
	 * @param n
	 */
	public closestTo(n: number): number | null {
		const distanceMap = [];
		this._list.forEach((item, index) => {
			distanceMap.push({
				index: index,
				distance: Math.abs(n - this.valueFor(item)),
			});
		});

		const closest = distanceMap.reduce((closest, current) => {
			if (closest.distance < current.distance) return current;
			else return closest;
		}, distanceMap[0] || null);

		return closest.index ?? null;
	}
}
